import { useTranslations } from "next-intl";

import { FormButton } from "@/components/form/FormButton";
import { Icon } from "@/components/icons/Icon";
import { formatDollarAmount } from "@/utils/formatDollars";
import { twMerge } from "@/utils/twMerge";

function PlanPrice({ cents, suffix }: { cents?: number; suffix?: string }) {
  return (
    <div className="h-8 leading-none">
      {cents !== undefined && (
        <>
          <span className="text-[32px] tracking-tighter">
            $<span className="font-bold">{formatDollarAmount(cents)}</span>
          </span>
          <span className="text-size-14 tracking-tighter text-gray-500"> {suffix}</span>
        </>
      )}
    </div>
  );
}

function PlanFeatures({ title, features }: { title: string; features: string[] }) {
  return (
    <div className="flex flex-col gap-2 text-size-15 tracking-tighter">
      <div className="font-semibold">{title}</div>
      {features.map((feature) => (
        <div key={feature} className="flex items-center gap-1.5">
          <Icon name="Check" className="w-[18px] h-[18px] shrink-0 text-blue-500" />
          {feature}
        </div>
      ))}
    </div>
  );
}

export type PlanAction =
  | {
      label: string;
      onClick: () => void;
      isBusy?: boolean;
      isDisabled?: boolean;
      isMuted?: boolean;
    }
  | { label: string; href: string };

const actionButtonClassName = "w-full h-full rounded-8 text-size-14 tracking-tighter";

function PlanButton({
  isCurrent,
  currentLabel,
  action,
}: {
  isCurrent: boolean;
  currentLabel?: string;
  action?: PlanAction;
}) {
  const t = useTranslations();

  if (isCurrent) {
    return (
      <FormButton
        variant="solid"
        size="sm"
        className={twMerge(actionButtonClassName, "opacity-100 pointer-events-none")}
        disabled
      >
        {currentLabel ?? t("upgradePlanDialog.currentPlan")}
      </FormButton>
    );
  }

  if (!action) return null;

  if ("href" in action) {
    return (
      <a
        href={action.href}
        className={twMerge(
          "flex items-center justify-center bg-block-bg text-blue-500",
          "ring-2 ring-inset ring-blue-100 dark:ring-blue-900",
          "hover:bg-blue-100 dark:hover:bg-gray-900",
          actionButtonClassName,
        )}
      >
        {action.label}
      </a>
    );
  }
  return (
    <FormButton
      variant="accent"
      size="sm"
      className={twMerge(
        actionButtonClassName,
        action.isMuted && "bg-gray-200 text-gray-500 dark:bg-gray-800",
        action.isMuted && !action.isDisabled && "hover:bg-gray-300 dark:hover:bg-gray-700",
      )}
      isBusy={action.isBusy}
      disabled={action.isDisabled}
      onClick={action.onClick}
    >
      {action.label}
    </FormButton>
  );
}

export function PlanCard({
  name,
  description,
  cents,
  priceSuffix,
  isCurrent,
  currentLabel,
  action,
  featuresTitle,
  features,
}: {
  name: string;
  description: string;
  cents?: number;
  priceSuffix?: string;
  isCurrent: boolean;
  currentLabel?: string;
  action?: PlanAction;
  featuresTitle: string;
  features: string[];
}) {
  return (
    <section
      aria-label={name}
      className="flex flex-col gap-2.5 p-2.5 rounded-12 bg-gray-100 dark:bg-gray-900"
    >
      <div className="flex flex-col p-2.5 pb-0">
        <h3 className="text-size-20 tracking-tighter leading-none font-semibold">{name}</h3>
        <p className="text-size-14 tracking-tighter mb-8 text-gray-500">{description}</p>
        <PlanPrice cents={cents} suffix={priceSuffix} />
      </div>
      <div className="h-10">
        <PlanButton isCurrent={isCurrent} currentLabel={currentLabel} action={action} />
      </div>
      <PlanFeatures title={featuresTitle} features={features} />
    </section>
  );
}
